import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, Outlet, useLocation, Navigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { AppBar, Toolbar, Button, Container, Typography, Box, IconButton, Menu, MenuItem, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import SearchIcon from "@mui/icons-material/Search";

import Login from "./components/Login";
import Register from "./components/Register";
import ProtectedRoute from "./components/ProtectedRoute";
import Dashboard from "./components/Dashboard";
import GuestImages from "./components/GuestImages";
import SavedSubmissions from "./components/SavedSubmissions";
import DummyEntry from "./components/DummyEntry";
import DummySubmissions from "./components/DummySubmissions";
import DayAmountTransactions from "./components/DayAmountTransactions";
import AdvanceDataReport from "./components/AdvanceDataReport";
import RoomLedgerReport from "./components/RoomLedgerReport";
import CustomerVisits from "./components/CustomerVisits";
import CustomerVisitsHistory from "./components/CustomerVisitsHistory";
import BreakfastComplimentary from "./components/BreakfastComplimentary";
import { logoutUser } from "./redux/actions/loginAction";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { createTheme, ThemeProvider } from "@mui/material/styles";

const theme = createTheme({
  components: {
    MuiTextField: {
      defaultProps: {
        inputProps: {
          style: { textTransform: "uppercase" },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          textTransform: "uppercase",
        },
      },
    },
  },
});

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function TopBar() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoggedIn } = useSelector((state) => state.login || {});
  const { pathname } = useLocation();
  // the tab of the page you are on is highlighted
  const isActive = (to) => pathname === to || pathname.startsWith(`${to}/`);
  const activeTabSx = { bgcolor: "rgba(255,255,255,0.22)", borderBottom: "3px solid #ffd700", borderRadius: "4px 4px 0 0", fontWeight: "bold" };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [anchorEl, setAnchorEl] = React.useState(null);
  // Customer Visits History form (same as the main hotel app), all logged-in users
  const [guestSearchOpen, setGuestSearchOpen] = React.useState(false);

  const handleLogout = () => {
    setAnchorEl(null);
    localStorage.removeItem("token");
    dispatch(logoutUser());
    navigate("/login", { replace: true });
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const navLinks = isLoggedIn ? [
    { label: "Add Guest Image", to: "/guest-images" },
    { label: "Saved Submissions", to: "/saved-submissions" },
    { label: "Dummy Submissions", to: "/dummy-submissions" },
    { label: "Breakfast Complimentary", to: "/breakfast-complimentary" },
  ] : [
    { label: "Login", to: "/login" },
  ];

  return (
    <AppBar position="static" color="primary" elevation={2}>
      <Toolbar>
        {isMobile && (
          <>
            <IconButton
              size="large"
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={handleMenu}
              sx={{ mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
            >
              {isLoggedIn && (
                <MenuItem onClick={() => { handleClose(); setGuestSearchOpen(true); }}>
                  <SearchIcon fontSize="small" sx={{ mr: 1 }} /> Guest Search
                </MenuItem>
              )}
              {navLinks.map((link) => (
                <MenuItem
                  key={link.to}
                  component={Link}
                  to={link.to}
                  onClick={handleClose}
                  selected={isActive(link.to)}
                  sx={isActive(link.to) ? { fontWeight: "bold", color: "primary.main" } : {}}
                >
                  {link.label}
                </MenuItem>
              ))}
              {isLoggedIn && (
                <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                  Logout
                </MenuItem>
              )}
            </Menu>
          </>
        )}

        <Typography 
          variant="h6" 
          component={Link} 
          to="/" 
          sx={{ textDecoration: 'none', color: 'inherit', fontWeight: 'bold', flexGrow: isMobile ? 1 : 0 }}
        >
          Hotel Management
        </Typography>

        {!isMobile && <Box sx={{ flexGrow: 1 }} />}

        {!isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isLoggedIn && (
              <Button color="inherit" startIcon={<SearchIcon />} onClick={() => setGuestSearchOpen(true)}>
                Guest Search
              </Button>
            )}
            {navLinks.map((link) => (
              <Button
                key={link.to}
                color="inherit"
                component={Link}
                to={link.to}
                sx={isActive(link.to) ? activeTabSx : { borderBottom: "3px solid transparent" }}
              >
                {link.label}
              </Button>
            ))}
            {isLoggedIn && (
              <Button 
                color="inherit" 
                onClick={handleLogout}
                variant="outlined"
                sx={{ ml: 1, borderColor: 'rgba(255,255,255,0.5)' }}
              >
                Logout
              </Button>
            )}
          </Box>
        )}
      </Toolbar>
      {isLoggedIn && (
        <CustomerVisitsHistory open={guestSearchOpen} onClose={() => setGuestSearchOpen(false)} />
      )}
    </AppBar>
  );
}

function PublicLayout() {
  return (
    <>
      <TopBar />
      <Container disableGutters sx={{ mt: { xs: 1.5, md: 3 }, px: { xs: 1, sm: 3 } }}>
        <Outlet />
      </Container>
    </>
  );
}

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <Router>
        <ScrollToTop />
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/guest-images"
              element={
                <ProtectedRoute>
                  <GuestImages />
                </ProtectedRoute>
              }
            />

            <Route
              path="/saved-submissions"
              element={
                <ProtectedRoute>
                  <SavedSubmissions />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dummy-entry"
              element={
                <ProtectedRoute>
                  <DummyEntry />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dummy-submissions"
              element={
                <ProtectedRoute>
                  <DummySubmissions />
                </ProtectedRoute>
              }
            />

            <Route
              path="/day-amount-transactions"
              element={
                <ProtectedRoute>
                  <DayAmountTransactions />
                </ProtectedRoute>
              }
            />

            <Route
              path="/advance-data-report"
              element={
                <ProtectedRoute>
                  <AdvanceDataReport />
                </ProtectedRoute>
              }
            />

            <Route
              path="/room-ledger-report"
              element={
                <ProtectedRoute>
                  <RoomLedgerReport />
                </ProtectedRoute>
              }
            />

            <Route
              path="/breakfast-complimentary"
              element={
                <ProtectedRoute>
                  <BreakfastComplimentary />
                </ProtectedRoute>
              }
            />

            <Route
              path="/customer-visits"
              element={
                <ProtectedRoute>
                  <CustomerVisits />
                </ProtectedRoute>
              }
            />

            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Navigate to="/guest-images" replace />
                </ProtectedRoute>
              }
            />
          </Route>

          <Route path="*" element={<Navigate to="/guest-images" replace />} />
        </Routes>
      </Router>
      <ToastContainer position="top-right" autoClose={3000} />
    </ThemeProvider>
  );
}
